/**
 * Deploy Capital Wizard page.
 *
 * A 4-step wizard that helps users deploy capital toward investment theses:
 * 1. Select thesis (highlights under-allocated)
 * 2. Enter deployment amount
 * 3. AI suggests candidate holdings
 * 4. Review and link to Buy Simulator
 *
 * Accepts ?thesis_id= query param to pre-select a thesis.
 */
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../components/common'
import { WizardSteps } from '../components/deploy/WizardSteps'
import { ThesisSelector } from '../components/deploy/ThesisSelector'
import { AmountInput } from '../components/deploy/AmountInput'
import { CandidatesList } from '../components/deploy/CandidatesList'
import { ReviewConfirm } from '../components/deploy/ReviewConfirm'
import { useThesisAllocations, useDeploymentSuggestions } from '../hooks'
import { usePortfolioFilter } from '../contexts/PortfolioFilterContext'
import type { DeploymentCandidate } from '../types/deploy'

type WizardStep = 1 | 2 | 3 | 4

interface WizardState {
  step: WizardStep
  selectedThesisId: string | null
  deploymentAmount: number
  candidates: DeploymentCandidate[]
  selectedCandidates: string[]
}

const STEPS = [
  { label: 'Thesis', completed: false },
  { label: 'Amount', completed: false },
  { label: 'Candidates', completed: false },
  { label: 'Review', completed: false },
]

export function DeployCapitalPage() {
  const [searchParams] = useSearchParams()
  const { selectedPortfolioId } = usePortfolioFilter()

  // Get thesis allocations
  const { data: allocationsData, isLoading: allocationsLoading } = useThesisAllocations(
    selectedPortfolioId
  )

  // AI suggestions mutation
  const suggestionsMutation = useDeploymentSuggestions()

  // Initialize from query param
  const initialThesisId = searchParams.get('thesis_id')

  // Wizard state
  const [state, setState] = useState<WizardState>({
    step: initialThesisId ? 2 : 1, // Skip to step 2 if thesis pre-selected
    selectedThesisId: initialThesisId,
    deploymentAmount: 0,
    candidates: [],
    selectedCandidates: [],
  })

  // Get selected thesis allocation
  const selectedThesis = useMemo(() => {
    if (!state.selectedThesisId || !allocationsData) return null
    return allocationsData.allocations.find((a) => a.thesis_id === state.selectedThesisId) ?? null
  }, [state.selectedThesisId, allocationsData])

  // Calculate portfolio value
  const portfolioValue = allocationsData?.total_portfolio_value ?? 0

  // Update step completion status
  const stepsWithStatus = useMemo(() => {
    return STEPS.map((s, i) => ({
      ...s,
      completed:
        i === 0 ? !!state.selectedThesisId :
        i === 1 ? state.deploymentAmount > 0 :
        i === 2 ? state.selectedCandidates.length > 0 :
        false,
    }))
  }, [state.selectedThesisId, state.deploymentAmount, state.selectedCandidates])

  // Handle thesis selection
  const handleThesisSelect = (thesisId: string) => {
    setState((prev) => ({
      ...prev,
      selectedThesisId: thesisId,
      // Reset candidates when changing thesis
      candidates: [],
      selectedCandidates: [],
    }))
  }

  // Handle amount change
  const handleAmountChange = (amount: number) => {
    setState((prev) => ({
      ...prev,
      deploymentAmount: amount,
      // Reset candidates when amount changes significantly
      candidates: [],
      selectedCandidates: [],
    }))
  }

  // Handle candidate toggle
  const handleCandidateToggle = (symbol: string) => {
    setState((prev) => ({
      ...prev,
      selectedCandidates: prev.selectedCandidates.includes(symbol)
        ? prev.selectedCandidates.filter((s) => s !== symbol)
        : [...prev.selectedCandidates, symbol],
    }))
  }

  // Trigger AI suggestions when moving to step 3
  useEffect(() => {
    if (
      state.step === 3 &&
      state.selectedThesisId &&
      state.deploymentAmount > 0 &&
      state.candidates.length === 0 &&
      !suggestionsMutation.isPending
    ) {
      suggestionsMutation.mutate(
        {
          thesis_id: state.selectedThesisId,
          deployment_amount: state.deploymentAmount,
          portfolio_id: selectedPortfolioId ?? undefined,
        },
        {
          onSuccess: (data) => {
            setState((prev) => ({
              ...prev,
              candidates: data.candidates,
              // Auto-select all candidates
              selectedCandidates: data.candidates.map((c) => c.symbol),
            }))
          },
        }
      )
    }
  }, [state.step, state.selectedThesisId, state.deploymentAmount, selectedPortfolioId])

  // Navigation
  const canGoNext =
    state.step === 1 ? !!state.selectedThesisId :
    state.step === 2 ? state.deploymentAmount > 0 :
    state.step === 3 ? state.selectedCandidates.length > 0 :
    false

  const goNext = () => {
    if (canGoNext && state.step < 4) {
      setState((prev) => ({ ...prev, step: (prev.step + 1) as WizardStep }))
    }
  }

  const goBack = () => {
    if (state.step > 1) {
      setState((prev) => ({ ...prev, step: (prev.step - 1) as WizardStep }))
    }
  }

  // Get selected candidates as full objects
  const selectedCandidateObjects = useMemo(() => {
    return state.candidates.filter((c) => state.selectedCandidates.includes(c.symbol))
  }, [state.candidates, state.selectedCandidates])

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Deploy Capital</h1>
        <p className="mt-1 text-sm text-gray-500">
          Get AI-powered suggestions for deploying capital toward your investment theses.
        </p>
      </div>

      {/* Step indicator */}
      <WizardSteps currentStep={state.step} steps={stepsWithStatus} />

      {/* Step content */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        {state.step === 1 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Select a Thesis
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Choose which investment thesis you want to deploy capital toward.
              Under-allocated theses are highlighted.
            </p>
            <ThesisSelector
              allocations={allocationsData?.allocations ?? []}
              selectedThesisId={state.selectedThesisId}
              onSelect={handleThesisSelect}
              isLoading={allocationsLoading}
            />
          </>
        )}

        {state.step === 2 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Enter Deployment Amount
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              How much capital do you want to deploy toward this thesis?
            </p>
            <AmountInput
              amount={state.deploymentAmount}
              onChange={handleAmountChange}
              thesis={selectedThesis}
              portfolioValue={portfolioValue}
            />
          </>
        )}

        {state.step === 3 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              AI-Generated Candidates
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Based on your thesis narrative, here are suggested holdings to consider.
            </p>
            <CandidatesList
              candidates={state.candidates}
              selectedSymbols={state.selectedCandidates}
              onToggle={handleCandidateToggle}
              isLoading={suggestionsMutation.isPending}
              error={suggestionsMutation.error?.message}
            />
          </>
        )}

        {state.step === 4 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Review & Simulate
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Review your selections and simulate purchases in the Buy Simulator.
            </p>
            <ReviewConfirm
              thesis={selectedThesis}
              amount={state.deploymentAmount}
              selectedCandidates={selectedCandidateObjects}
            />
          </>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <Button
          variant="secondary"
          onClick={goBack}
          disabled={state.step === 1}
        >
          Back
        </Button>

        {state.step < 4 && (
          <Button
            variant="primary"
            onClick={goNext}
            disabled={!canGoNext || (state.step === 2 && suggestionsMutation.isPending)}
          >
            {state.step === 2 && suggestionsMutation.isPending
              ? 'Generating...'
              : 'Next'}
          </Button>
        )}

        {state.step === 4 && (
          <Button
            variant="secondary"
            onClick={() =>
              setState({
                step: 1,
                selectedThesisId: null,
                deploymentAmount: 0,
                candidates: [],
                selectedCandidates: [],
              })
            }
          >
            Start Over
          </Button>
        )}
      </div>
    </div>
  )
}
